import React from 'react';
import './RegisterCardPopup.style.css';
import { useUserDataAction, useUserStack } from 'store';
import { Spin, Input } from 'antd';
import { Content, Row, Col, Text, Button } from 'components';
import { CheckSquareFilled, CheckSquareOutlined } from '@ant-design/icons';
import {
  usePaymentDeRegistBilling,
  usePaymentRegistBilling,
} from 'hooks/PaymentHooks.';
import { useCreateCard } from 'hooks/CardHooks';
import MessageAlert, { PAYMENT_STORE_ID } from 'utils';

const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
};

const RegisterCardPopup = (props) => {
  /* ===== Route ===== */
  /* ===== Props ===== */
  const { searchParams } = props;

  const type = searchParams.get('type');

  /* ===== Mutate ===== */
  const { registBilling } = usePaymentRegistBilling();
  const { deregistBilling } = usePaymentDeRegistBilling();
  const { createCard } = useCreateCard();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const userDataAction = useUserDataAction();

  /* ===== State ===== */
  const [isLoading, setIsLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState({
    card_number: '',
    MM: '',
    YY: '',
    cvc: '',
    card_password: '',
    birthday: '',
    information_provision: 'N',
  });

  /* ===== Hooks ===== */
  /* ===== Function ===== */
  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback = null) => {
    setInputValue((prevState) => {
      const newData = { ...prevState, [t]: v };

      if (callback) callback(v);

      return newData;
    });
  };

  /**
   * 카드 등록 유효성 검사
   * --
   * @returns {boolean}
   */
  const handleValidateCheck = () => {
    const {
      card_number,
      MM,
      YY,
      cvc,
      card_password,
      birthday,
      information_provision,
    } = inputValue;

    if (!card_number || card_number.length < 16) {
      MessageAlert.info('카드번호 16자리를 입력해주세요.');
      return false;
    }

    if (!MM || MM.length < 2 || !YY || YY.length < 2) {
      MessageAlert.info('카드 유효기간 입력해주세요!');
      return;
    }

    if (cvc.length < 2 || !cvc) {
      MessageAlert.info('CVC 번호 3자리를 입력해주세요!');
      return;
    }

    if (card_password.length < 2 || !card_password) {
      MessageAlert.info('카드 비밀번호 앞 2자리를 입력해주세요!');
      return;
    }

    if (!birthday) {
      MessageAlert.info('생년월일을 입력해주세요!');
      return;
    }

    if (birthday.length < 6) {
      MessageAlert.info('생년월일 6자리를 입력해주세요!');
      return;
    }

    if (information_provision === 'N') {
      MessageAlert.info('이용약관에 동의해주세요!');
      return;
    }

    return true;
  };

  /**
   * 카드생성
   * --
   */
  const handleSubmit = async () => {
    if (!handleValidateCheck()) return;

    try {
      setIsLoading(true);

      // 빌링카드 등록 데이터
      const billingData = {
        mid: PAYMENT_STORE_ID, // TODO: 충전 상점키
        cardNumb: `${inputValue.card_number}`,
        expiryDate: `${inputValue.YY}${inputValue.MM}`,
        password2: `${inputValue.card_password}`,
        userInfo: `${inputValue.birthday}`,
      };

      const { status, data } = await registBilling(billingData);

      // 빌링카드 등록 실패 처리
      if (status !== 200 || data.code !== 'A0200') {
        MessageAlert.warning(`카드 등록에 실패 하였습니다!`);

        return;
      }

      const { billingToken, maskedCardNumb, issuerCardType, issuerCardName } =
        data.data;

      // 카드 등록 데이터
      const createCardData = {
        user_id: userInfo?.user_id,
        type: type.toLocaleUpperCase(),
        card_number: maskedCardNumb,
        card_validity: `${inputValue.MM}${inputValue.YY}`,
        billing_token: billingToken,
        card_company: `${issuerCardType} ${issuerCardName}`,
      };

      // 카드 등록
      const res = await createCard(createCardData);

      // 카드 등록 실패 처리
      if (res.status !== 200) {
        await deregistBilling({
          mid: PAYMENT_STORE_ID,
          billingToken: billingToken,
        });
        MessageAlert.warning(`카드 등록에 실패 하였습니다!`);
        return;
      }

      const registerData = {
        data: JSON.stringify(res.data),
        source: 'card-register',
      };

      if (window.opener) {
        window.opener.postMessage(registerData, '*');
      } else {
        userDataAction.addData('cardList', res.data);
      }

      MessageAlert.success('카드가 성공적으로 등록되었습니다!');
      setTimeout(() => window.close(), 500);
    } catch (err) {
      console.error('[handleSubmit] Error!! : ', err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /* ===== Variable ===== */
  /* ===== Hooks ===== */

  /* ===== Render ===== */
  return (
    <Spin spinning={isLoading}>
      <Content
        maxWidth={'100%'}
        style={{ height: 700, position: 'relative', top: 0, left: 0 }}
      >
        <Row>
          <Col x={24}>
            <Content maxWidth={'100%'} padding={0}>
              <Text size={18} weight={700}>
                카드 추가
              </Text>
            </Content>
          </Col>
        </Row>

        <Row>
          <Col x={24}>
            <Content
              maxWidth={'100%'}
              padding={0}
              style={{
                marginTop: 30,
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
              }}
            >
              <div style={{ ...styles.form }}>
                <Text size={14}>카드번호</Text>
                <Input.Password
                  size="large"
                  placeholder="카드번호를 입력해주세요"
                  value={inputValue.card_number}
                  onChange={(e) => handleChange('card_number', e.target.value)}
                  maxLength={16}
                />
              </div>

              <div style={{ ...styles.form }}>
                <Text size={14}>유효기간</Text>

                <div style={{ ...styles.form, flexDirection: 'row' }}>
                  <Input
                    size="large"
                    placeholder="MM"
                    maxLength={2}
                    value={inputValue.MM}
                    onChange={(e) => handleChange('MM', e.target.value)}
                  />
                  <Input
                    size="large"
                    placeholder="YY"
                    maxLength={2}
                    value={inputValue.YY}
                    onChange={(e) => handleChange('YY', e.target.value)}
                  />
                </div>
              </div>

              <div style={{ ...styles.form }}>
                <div style={{ ...styles.form, flexDirection: 'row' }}>
                  <div style={{ ...styles.form, flex: 1 }}>
                    <Text size={14}>CVC</Text>

                    <Input.Password
                      size="large"
                      placeholder="CVC"
                      maxLength={3}
                      value={inputValue.cvc}
                      onChange={(e) => handleChange('cvc', e.target.value)}
                    />
                  </div>
                  <div style={{ ...styles.form, flex: 1 }}>
                    <Text size={14}>비밀번호</Text>

                    <Input.Password
                      size="large"
                      placeholder="비밀번호 앞 2자리"
                      maxLength={2}
                      value={inputValue.card_password}
                      onChange={(e) =>
                        handleChange('card_password', e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div style={{ ...styles.form }}>
                <Text size={14}>생년월일</Text>
                <Input
                  size="large"
                  placeholder="생년월일 6자리를 입력해주세요"
                  value={inputValue.birthday}
                  onChange={(e) => handleChange('birthday', e.target.value)}
                />
              </div>
            </Content>
          </Col>
        </Row>

        <Row>
          <Col x={24}>
            <Content
              maxWidth={'100%'}
              padding={0}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                marginTop: 30,
              }}
            >
              <p style={{ fontSize: 14, wordBreak: 'break-word' }}>
                정기배송(결제)을 위해 등록되는/등록된 카드 정보는 PG사에
                안전하게 보관되며, 이후 배송주기별 결제 시점에 결제를 위한
                정보로 사용됩니다.
              </p>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                <button
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: 24,
                  }}
                  onClick={() =>
                    handleChange(
                      'information_provision',
                      inputValue.information_provision === 'Y' ? 'N' : 'Y'
                    )
                  }
                >
                  {inputValue.information_provision === 'Y' ? (
                    <CheckSquareFilled style={{ color: '#111111' }} />
                  ) : (
                    <CheckSquareOutlined style={{ color: '#BDBDBD' }} />
                  )}
                </button>
                <p>
                  위 내용을 확인했으며,
                  <br /> 개인정보 제 3자 제공 내역 및 결제에 동의합니다.
                </p>
              </div>
            </Content>
          </Col>
        </Row>

        <Button
          style={{
            width: 'calc(100% - 30px)',
            borderRadius: 0,
            position: 'absolute',
            bottom: 20,
          }}
          onClick={() => handleSubmit()}
        >
          추가하기
        </Button>
      </Content>
    </Spin>
  );
};

export default RegisterCardPopup;
